import { useGetGlobalDataFreshnessQuery } from '@autone/redux/data-ingestion';
import { useAutoneTranslation } from '@autone/translations';
import { useMemo } from 'react';

import { type SidebarNavigationItemProps } from '../../types/Sidebar.types';
import { createUserCardItem } from '../../utilities/createUserCardItem';
import { useSidebarAccount } from '../use-sidebar-account';
import { useSidebarModules } from '../use-sidebar-modules';
import { useSidebarProfile } from '../use-sidebar-profile';
import { useUserProfile } from '../useUserProfile';

import {
  type SidebarSectionConfig,
  type UseAppSidebarReturn,
} from './useAppSidebar.types';

/**
 * Custom hook for managing application sidebar configuration
 *
 * This hook provides a complete sidebar configuration including:
 * - Navigation sections (hub, modules, account)
 * - User card dropdown items
 * - Proper authorization checks
 * - Active state detection
 * - Internationalization support
 *
 * @returns Object containing sidebar configuration and user card configuration
 *
 * @example
 * ```tsx
 * function Sidebar() {
 *   const { config, userCardConfig } = useAppSidebar();
 *
 *   return (
 *     <nav>
 *       <SidebarSection section={config.hub} />
 *       <SidebarSection section={config.modules} />
 *       <SidebarSection section={config.account} />
 *       <UserDropdown items={userCardConfig} />
 *     </nav>
 *   );
 * }
 * ```
 */
export const useAppSidebar = (): UseAppSidebarReturn => {
  const { common } = useAutoneTranslation();
  const userProfile = useUserProfile();

  const moduleConfig = useSidebarModules();
  const accountConfig = useSidebarAccount();
  const profileConfig = useSidebarProfile();
  const globalDataFreshness = useGetGlobalDataFreshnessQuery({
    extraArgs: { hideSnackbar: true },
  });

  /**
   * Main sidebar configuration
   */
  const config = useMemo<SidebarSectionConfig>((): SidebarSectionConfig => {
    return {
      ...moduleConfig,
      ...accountConfig,
    };
  }, [moduleConfig, accountConfig]);

  /**
   * User card dropdown configuration with memoization
   */
  const userCardConfig = useMemo(
    (): SidebarNavigationItemProps[] =>
      profileConfig.map((itemConfig) => createUserCardItem(itemConfig, common)),
    [common, profileConfig],
  );

  return {
    config,
    userCardConfig,
    userProfile,
    globalDataFreshness: {
      data: globalDataFreshness.data,
      isLoading: globalDataFreshness.isLoading,
      isFetching: globalDataFreshness.isFetching,
      error: globalDataFreshness.error,
    },
  };
};
